/**
 * Failure classification for the Pendo events (spec 094).
 *
 * ── Why this is its own module ──────────────────────────────────────────────
 * It lived in `./reportFacts.js` until `SpecializedReportShell` needed it, and
 * that produced a real circular import:
 *
 *   registry.js → ARReport.jsx → SpecializedReportShell.jsx
 *               → reportFacts.js → registry.js
 *
 * `reportFacts` imports `pages/reports/registry` for `reportSurface`, and the
 * registry statically imports all 24 specialized page components — so loading a
 * specialized page first left `REPORT_RENDERERS.cbARReportView` **undefined**,
 * silently breaking the renderer lookup for every specialized report. Caught by
 * `specializedReports.test.jsx`; it is the same hazard `pendo.js` is kept free
 * of app imports to avoid.
 *
 * So this file is a LEAF: its only import is `@utils/reportProgress`, which
 * imports nothing. Anything a shared component needs belongs here, not in
 * `reportFacts`.
 */

import { isCapacityMessage, isTimeoutMessage } from '@utils/reportProgress'

/**
 * Classify a failed run into a small enum.
 *
 * 🔴 The MESSAGE never leaves the browser. CB's terminal strings have been
 * observed carrying a server filesystem path, an internal volume layout and a
 * tenant directory name (CC-49993), and its 5xx bodies carry SQL exception
 * text. This reads the message only to TEST ITS SHAPE, via the repo's own
 * `isTimeoutMessage` / `isCapacityMessage` predicates, and returns a token.
 *
 * `capacity` is an addition to the enum named in the tracking plan, and a
 * deliberate one: CB's "this report is too large" bail-out is the single most
 * actionable run-failure class in this product, it arrives as prose with no
 * status code, and folding it into `unknown` would throw it away. Track Events
 * are not retroactive, so a class omitted now is unrecoverable.
 *
 * @param {Error|object|string} err an axios error, a thrown Error, or a bare
 *   message string — the 20 specialized pages store their failure as a STRING
 *   (`setError(err?.response?.data?.error || err.message || '…')`) and hand
 *   that to SpecializedReportShell, so by then there is no object left to
 *   inspect. Accepting a string keeps `timeout` and `capacity` detectable there
 *   instead of collapsing the whole specialized surface into `unknown`.
 * @returns {'timeout'|'permission'|'capacity'|'empty_body'|'http_500'|'network'|'unknown'}
 */
export function errorClass(err) {
  const code = err?.code
  const status = err?.response?.status
  const message = typeof err === 'string' ? err : err?.message
  const body = err?.response?.data
  const bodyErr = body?.error || body?.message

  if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') return 'timeout'
  if (status === 408 || status === 504 || status === 524) return 'timeout'
  if (isTimeoutMessage(message) || isTimeoutMessage(bodyErr)) return 'timeout'

  if (status === 401 || status === 403) return 'permission'

  // Capacity arrives as prose through the job status with no HTTP status at all
  // (`throw new Error(st.error)`), so it has to be tested before the transport
  // branches below.
  if (isCapacityMessage(message) || isCapacityMessage(bodyErr)) return 'capacity'

  if (typeof status === 'number' && status >= 500) {
    // CB writes the finished report to a file keyed by processId and a fast
    // report reports 100% a beat BEFORE that file flushes, so an immediate
    // fetch 500s with nothing in the body. Distinct from a real server error.
    return bodyErr ? 'http_500' : 'empty_body'
  }

  // An axios error with no `response` never reached a server: DNS, CORS, an
  // offline tab, a blocked request.
  if (err && Object.prototype.hasOwnProperty.call(err, 'response') && !err.response) return 'network'
  if (code === 'ERR_NETWORK') return 'network'

  return 'unknown'
}

/**
 * HTTP status as a GROUPABLE string ('' when there wasn't one).
 *
 * Pendo cannot Group By a numeric property, and a status code carries no firm
 * data. Sending it as a string keeps 4xx detail (404 = the result expired,
 * 400 = a bad filter) answerable without growing `errorClass`.
 */
export const httpStatusToken = (err) => {
  const status = err?.response?.status
  return typeof status === 'number' ? String(status) : ''
}
